import moment from "moment";

const DATE_FORMAT = "YYYY-MM-DD";

/** An absence as the calendar edits it: both `start` and `end` are inclusive days. */
export interface AbsencePeriod {
  id: string;
  loAType?: string;
  comment?: string;
  start: string;
  end: string;
}

export type AbsenceSplitOperation =
  | { kind: "delete"; absence: AbsencePeriod }
  | { kind: "shorten"; absence: AbsencePeriod; start: string; end: string }
  | { kind: "create"; source: AbsencePeriod; start: string; end: string };

export interface AbsenceSplitApi {
  /** Creates an absence with inclusive `start`/`end` and resolves to its new id. */
  create: (absence: Omit<AbsencePeriod, "id">) => Promise<string | undefined>;
  /** Updates an absence to the given inclusive `start`/`end`. */
  update: (absence: AbsencePeriod) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

const shiftDay = (date: string, days: number) => moment(date, DATE_FORMAT).add(days, "day").format(DATE_FORMAT);

/**
 * Determines how existing absences have to be shortened, split or removed so the new
 * period wins exactly its own days while every other day of those absences survives.
 */
export const planAbsenceSplit = (
  existingAbsences: AbsencePeriod[],
  newPeriod: { start: string; end: string },
  ignoreId?: string
): AbsenceSplitOperation[] => {
  const operations: AbsenceSplitOperation[] = [];

  existingAbsences.forEach((absence) => {
    if (absence.id === ignoreId) {
      return;
    }
    const overlaps = absence.start <= newPeriod.end && newPeriod.start <= absence.end;
    if (!overlaps) {
      return;
    }

    const keepsHead = absence.start < newPeriod.start;
    const keepsTail = absence.end > newPeriod.end;

    if (!keepsHead && !keepsTail) {
      operations.push({ kind: "delete", absence });
      return;
    }
    if (keepsHead) {
      operations.push({ kind: "shorten", absence, start: absence.start, end: shiftDay(newPeriod.start, -1) });
    }
    if (keepsTail) {
      if (keepsHead) {
        operations.push({ kind: "create", source: absence, start: shiftDay(newPeriod.end, 1), end: absence.end });
      } else {
        operations.push({ kind: "shorten", absence, start: shiftDay(newPeriod.end, 1), end: absence.end });
      }
    }
  });

  return operations;
};

/**
 * Runs the split and resolves to a compensating action restoring the previous state, so a
 * failure later in the save (e.g. creating the new absence) can leave nothing behind.
 * A failure during the split itself rolls back on its own and rethrows.
 */
export const applyAbsenceSplit = async (
  operations: AbsenceSplitOperation[],
  api: AbsenceSplitApi
): Promise<() => Promise<void>> => {
  const undoSteps: Array<() => Promise<void>> = [];
  const rollback = async () => {
    for (const undo of [...undoSteps].reverse()) {
      await undo();
    }
  };

  try {
    for (const operation of operations) {
      if (operation.kind === "delete") {
        const { absence } = operation;
        await api.remove(absence.id);
        undoSteps.push(async () => {
          await api.create({
            loAType: absence.loAType,
            comment: absence.comment,
            start: absence.start,
            end: absence.end,
          });
        });
      } else if (operation.kind === "shorten") {
        const { absence } = operation;
        await api.update({ ...absence, start: operation.start, end: operation.end });
        undoSteps.push(async () => {
          await api.update(absence);
        });
      } else {
        const createdId = await api.create({
          loAType: operation.source.loAType,
          comment: operation.source.comment,
          start: operation.start,
          end: operation.end,
        });
        if (createdId) {
          undoSteps.push(async () => {
            await api.remove(createdId);
          });
        }
      }
    }
  } catch (error) {
    await rollback();
    throw error;
  }

  return rollback;
};
